import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { clearPaywallPreviewCache } from "@/hooks/usePaywallPreview";
import { ANGEL_BUSINESS_ID } from "@/constants";
import { Plus, Users, Trash2, Pencil } from "lucide-react";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";

interface Supplier {
  id: string;
  name: string;
  contact: string | null;
  category: string | null;
  default_lead_time_days: number | null;
}

/** Digits only + country code (e.g. 60 Malaysia). No + or spaces. */
function toWhatsAppNumber(contact: string | null): string | null {
  if (!contact) return null;
  const digits = contact.replace(/\D/g, "");
  if (digits.length < 9) return null;
  const withCountry = digits.length === 9 ? "60" + digits : digits.startsWith("0") ? "60" + digits.slice(1) : digits;
  return withCountry;
}

const openWhatsApp = (phone: string) => {
  const cleanNumber = phone.replace(/\D/g, "");
  const url = `https://wa.me/${cleanNumber}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function SuppliersPage() {
  const { user, businessId, role, refreshProfile, isFounder } = useAuth();
  const isReviewboostsServices = user?.email?.toLowerCase().trim() === "reviewboosts.services@gmail.com";
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<Supplier | null>(null);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [category, setCategory] = useState("");
  const [defaultLeadDays, setDefaultLeadDays] = useState("");
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSuppliers = async (overrideBusinessId?: string | null) => {
    const effectiveBizId = overrideBusinessId ?? businessId;
    if (!effectiveBizId) return;
    const baseQuery = supabase
      .from("suppliers")
      .select("*")
      .order("name")
      .eq("business_id", effectiveBizId);
    const { data } = await baseQuery;
    if (data) setSuppliers(data as Supplier[]);
  };

  useEffect(() => {
    fetchSuppliers();
  }, [businessId]);

  const resetForm = () => {
    setName("");
    setContact("");
    setCategory("");
    setDefaultLeadDays("");
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasWrongBusiness = isReviewboostsServices && businessId === ANGEL_BUSINESS_ID;
    const needEnsure =
      isReviewboostsServices && !isFounder ||
      (!businessId && !isFounder && (role === "admin" || isReviewboostsServices)) ||
      (isReviewboostsServices && !isFounder && (!businessId || hasWrongBusiness));
    if (needEnsure) {
      const { data: result, error: rpcError } = await supabase.rpc("insert_supplier_for_self", {
        p_name: name,
        p_contact: contact || null,
        p_category: category || null,
      });
      if (rpcError) {
        toast({
          title: "Error",
          description: rpcError.message || "Could not add supplier.",
          variant: "destructive",
        });
        return;
      }
      const res = result as { id?: string; business_id?: string } | null;
      if (res?.id && res?.business_id) {
        const newSupplier: Supplier = {
          id: res.id,
          name,
          contact: contact || null,
          category: category || null,
          default_lead_time_days: defaultLeadDays ? Number(defaultLeadDays) : null,
        };
        setSuppliers((prev) => [newSupplier, ...prev.filter((s) => s.id !== res.id)]);
        await refreshProfile();
        clearPaywallPreviewCache();
        setSuccessBanner("Supplier added successfully");
        setTimeout(() => setSuccessBanner(null), 4000);
        toast({ title: "Supplier Added" });
        resetForm();
        setAddOpen(false);
        fetchSuppliers(res.business_id);
        setTimeout(() => fetchSuppliers(res.business_id), 800);
      }
      return;
    }
    if (!businessId) {
      toast({
        title: "No business",
        description: "Your account is not linked to a business.",
        variant: "destructive",
      });
      return;
    }
    const { error } = await supabase.from("suppliers").insert({
      business_id: businessId ?? null,
      name,
      contact: contact || null,
      category: category || null,
      default_lead_time_days: defaultLeadDays ? Number(defaultLeadDays) : null,
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSuccessBanner("Supplier added successfully");
      setTimeout(() => setSuccessBanner(null), 4000);
      toast({ title: "Supplier Added" });
      resetForm();
      setAddOpen(false);
      fetchSuppliers();
    }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;
    const { error } = await supabase.from("suppliers").update({
      name,
      contact: contact || null,
      category: category || null,
      default_lead_time_days: defaultLeadDays ? Number(defaultLeadDays) : null,
    }).eq("id", editItem.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Supplier Updated" });
      setEditItem(null);
      resetForm();
      fetchSuppliers();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("suppliers").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchSuppliers();
    }
  };

  const filteredSuppliers = suppliers;

  return (
    <div className="space-y-6">
      {successBanner && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {successBanner}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Suppliers</h2>
          <p className="text-sm text-muted-foreground">Manage your vendors</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" /> Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required /></div>
              <div><Label>Contact</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone or email" /></div>
              <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Electronics, Food" /></div>
              <div><Label>Default lead time (days)</Label><Input type="number" min={1} max={120} value={defaultLeadDays} onChange={(e) => setDefaultLeadDays(e.target.value)} placeholder="e.g. 6" /><p className="text-xs text-muted-foreground mt-1">Used for reorder timing when this supplier&apos;s products appear on a purchase order.</p></div>
              <Button type="submit" className="w-full bg-primary text-primary-foreground">Save</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-primary" /> All Suppliers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSuppliers.length === 0 ? (
            suppliers.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">No suppliers yet.</p>
            ) : (
              <NoMatchingRecords />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-semibold">Name</th>
                    <th className="text-left py-3 px-2 font-semibold">Contact</th>
                    <th className="text-left py-3 px-2 font-semibold">Category</th>
                    <th className="text-left py-3 px-2 font-semibold">Lead days</th>
                    <th className="text-right py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((s) => {
                    const canWhatsApp = toWhatsAppNumber(s.contact);
                    return (
                    <tr key={s.id} className="border-b border-border/50">
                      <td className="py-3 px-2 font-medium">{s.name}</td>
                      <td className="py-3 px-2 text-muted-foreground">
                        {canWhatsApp ? (
                          <button
                            type="button"
                            onClick={() => openWhatsApp(toWhatsAppNumber(s.contact) ?? s.contact ?? "")}
                            className="text-primary hover:underline text-left"
                          >
                            {s.contact || "—"}
                          </button>
                        ) : (
                          s.contact || "—"
                        )}
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">{s.category || "—"}</td>
                      <td className="py-3 px-2 text-muted-foreground">{s.default_lead_time_days ?? "—"}</td>
                      <td className="py-3 px-2 text-right space-x-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => {
                          setEditItem(s);
                          setName(s.name);
                          setContact(s.contact || "");
                          setCategory(s.category || "");
                          setDefaultLeadDays(s.default_lead_time_days != null ? String(s.default_lead_time_days) : "");
                        }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(s.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-critical" />
                        </Button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) { setEditItem(null); resetForm(); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Supplier — {editItem?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required /></div>
            <div><Label>Contact</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} /></div>
            <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} /></div>
            <div><Label>Default lead time (days)</Label><Input type="number" min={1} max={120} value={defaultLeadDays} onChange={(e) => setDefaultLeadDays(e.target.value)} /><p className="text-xs text-muted-foreground mt-1">Used for reorder timing when this supplier&apos;s products appear on a purchase order.</p></div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
